import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Stack,
  Divider,
  Alert,
  Chip,
  Button,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "src/redux/store";
import axios from "src/utils/axios";
import { resolveFinalPrice } from "src/utils/pricing";
import useAuth from "src/hooks/useAuth";
import { getPlans } from "src/redux/slices/plan";
import { getAddOns } from "src/redux/slices/addOns";
import { readPendingSelection, savePendingSelection } from "src/utils/pendingSelection";
import { fetchOnboardingProgress } from "src/redux/slices/onboarding";

// ----------------------------------------------------------------------
//   Wizard · StepCheckout. Último paso antes del `StepFinish`.
//   Muestra el resumen de la selección (plan + add-ons + soporte) con
//   el total mensual estimado y un botón grande "Ir al pago" que crea
//   la Stripe Checkout Session (endpoint existente /payment/create) y
//   redirige a la URL segura de Stripe para que el user meta tarjeta.
//
//   Post-Stripe:
//     · Success → Stripe redirige al portal (/app/user/account/{code})
//       que a su vez marca la sub como activa.
//     · El wizard detecta al aterrizar (via localStorage flag) que hay
//       add-ons pendientes de aplicar y llama a SetAddOn por cada uno
//       (esto sí funciona porque la sub ya está activa).
//     · El wizard salta al `StepFinish` (bienvenida + QR).
//
//   Los primeros N add-ons incluidos gratis por el plan (Pro=1,
//   Premium=4) se aplicarán igual con SetAddOn — el precio 0 lo debe
//   gestionar el backend en la subscription (fuera del scope del
//   wizard v1). Por ahora, todos se contratan y el fundador ajusta
//   manualmente los que deban ser gratis desde admin/grants.
// ----------------------------------------------------------------------

const AFTER_CHECKOUT_KEY = "tmo_after_checkout_apply_addons";

function inferTier(plan) {
  const n = (plan?.name || "").toLowerCase();
  if (n.includes("premium")) return "premium";
  if (n.includes("pro")) return "pro";
  if (n.includes("starter")) return "starter";
  return null;
}

function includedCountFor(tier) {
  if (tier === "premium") return 4;
  if (tier === "pro") return 1;
  return 0;
}

export default function StepCheckout({ onComplete, onSkip }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { plans } = useSelector((s) => s.plan);
  const { addOns } = useSelector((s) => s.addOns);
  const [paying, setPaying] = useState(false);
  // `pendingVersion` fuerza re-lectura del localStorage cuando cambiamos
  // la selección desde este mismo step (botón "Cambiar plan/selección").
  const [pendingVersion, setPendingVersion] = useState(0);

  useEffect(() => {
    if (!plans || plans.length === 0) dispatch(getPlans(false));
    if (!addOns || addOns.length === 0) dispatch(getAddOns(true));
  }, [dispatch, plans, addOns]);

  const pending = useMemo(
    () => readPendingSelection() || {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingVersion],
  );

  const handleResetSelection = async () => {
    // Borramos planId del localStorage — el wizard, al re-renderizar,
    // detecta que no hay `pending.planId` y `forcedIdx` salta al step
    // `plan` para que el user vuelva a elegir. Preservamos addOnIds
    // y cycle por si quiere reutilizarlos con otro plan.
    const existing = readPendingSelection() || { addOnIds: [] };
    savePendingSelection({
      ...existing,
      planId: undefined,
      savedAt: new Date().toISOString(),
    });
    // Refrescamos el progress para que el `useOnboardingProgress` del
    // padre recompute el forcedIdx en el próximo render.
    setPendingVersion((v) => v + 1);
    await dispatch(fetchOnboardingProgress());
  };
  const plan = useMemo(
    () => (plans || []).find((p) => p.id === pending.planId) || null,
    [plans, pending.planId],
  );
  const selectedAddOns = useMemo(
    () =>
      (pending.addOnIds || [])
        .map((id) => (addOns || []).find((a) => a.id === id))
        .filter(Boolean),
    [pending.addOnIds, addOns],
  );

  const tier = inferTier(plan);
  const includedCount = includedCountFor(tier);

  // Non-support primero, luego support al final. Los primeros N no-support
  // están "incluidos con el plan" — el POST a Stripe los EXCLUYE (fix
  // 2026-08-13), por lo que el total mostrado tampoco debe sumarlos.
  // Antes sumaban al total visual pero al enviarlos filtrados a Stripe
  // se cobraba MENOS de lo mostrado (confuso al revés). Ahora total UI
  // == total Stripe.
  const nonSupport = selectedAddOns.filter((a) => !a.kind?.startsWith("support"));
  const supportAddOn = selectedAddOns.find((a) => a.kind?.startsWith("support"));

  const freeAddOns = nonSupport.slice(0, includedCount);   // etiqueta visual, NO se cobran
  const paidAddOns = nonSupport.slice(includedCount);       // se cobran a Stripe

  // Suma REAL con IVA por elemento — solo los que Stripe cobrará.
  const paidAddOnsCost = paidAddOns.reduce((s, a) => s + resolveFinalPrice(a), 0);
  const supportCost = resolveFinalPrice(supportAddOn);
  const planPriceWithIva = resolveFinalPrice(plan);
  const monthlyTotal = planPriceWithIva + paidAddOnsCost + supportCost;

  const handlePay = async () => {
    if (!plan) {
      enqueueSnackbar("Elige un plan antes de continuar", { variant: "warning" });
      return;
    }
    setPaying(true);
    try {
      // Enviamos SOLO los add-ons de PAGO (paidAddOns + support) a
      // Stripe. Los primeros N no-support (freeAddOns) están
      // "incluidos con tu plan" en la UI — Stripe NO debe cobrarlos.
      // Se guardan aparte en `AFTER_CHECKOUT_KEY.freeAddOnIds` para
      // que el post-checkout los active vía `SetAddOn` (que solo
      // actualiza cantidad, sin generar cargo — ver comentario del
      // handler post-pago). Sin este filtro Stripe cobraba TODO
      // aunque la UI dijera "Recomendado con tu plan".
      const paidAddOnIds = [
        ...paidAddOns.map((a) => a.id),
        ...(supportAddOn ? [supportAddOn.id] : []),
      ];
      const freeAddOnIds = freeAddOns.map((a) => a.id);
      const res = await axios.post("/payment/create", {
        IdPlan: plan.id,
        IdRestaurante: user?.id || window.localStorage.getItem("userId"),
        AddOnIds: paidAddOnIds,
      });
      const paymentLink = res?.data?.paymentLink;
      if (!paymentLink) {
        throw new Error("Stripe no devolvió URL de pago");
      }
      // Marcamos flag para que el post-checkout auto-aplique add-ons y
      // marque el step `checkout` como completado. NO marcamos el step
      // aquí — si el user cierra Stripe sin pagar y volvemos a marcar
      // completado, el wizard saltaría a `finish` sin sub activa. El
      // step queda pendiente hasta que Stripe confirme (PostCheckoutAutoApply
      // lo marca al detectar `paymentConfirmation.isActive === true`).
      try {
        localStorage.setItem(
          AFTER_CHECKOUT_KEY,
          JSON.stringify({
            addOnIds: (pending.addOnIds || []),      // TODOS (para SetAddOn quantity)
            paidAddOnIds,                             // solo los cobrados por Stripe
            freeAddOnIds,                             // "incluidos con tu plan" — activar sin cargo
            planId: plan.id,
            monthlyTotal,
            timestamp: new Date().toISOString(),
          }),
        );
      } catch {
        /* noop */
      }
      window.location.href = paymentLink;
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message || err?.message || "No pudimos iniciar el pago",
        { variant: "error" },
      );
      setPaying(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Confirma tu suscripción
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 3 }}>
        Revisa el resumen. Al pulsar <strong>Ir al pago</strong> te llevamos
        al checkout seguro de Stripe para introducir tu tarjeta. Cobramos
        solo cuando termine tu prueba de 30 días.
      </Typography>

      {!plan && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="warning"
              size="small"
              variant="contained"
              onClick={handleResetSelection}
              startIcon={<ArrowBackRoundedIcon />}
            >
              Elegir plan
            </Button>
          }
        >
          No hay plan seleccionado. Elige uno para continuar con el pago.
        </Alert>
      )}

      {plan && (
        <Card sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 700 }}>
              Resumen de tu suscripción
            </Typography>
            <Button
              size="small"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={handleResetSelection}
              sx={{ textTransform: "none" }}
            >
              Cambiar selección
            </Button>
          </Stack>
          <Stack spacing={2}>
            {/* Plan */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {plan.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Plan base — {pending.cycle === "annual" ? "anual" : "mensual"}
                  {plan?.includeIva && " · IVA incl."}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {planPriceWithIva.toFixed(2)} €
                <Typography component="span" variant="caption" sx={{ color: "text.secondary", ml: 0.5 }}>
                  /{pending.cycle === "annual" ? "año" : "mes"}
                </Typography>
              </Typography>
            </Stack>

            {(freeAddOns.length > 0 || paidAddOns.length > 0) && (
              <>
                <Divider />
                <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 700 }}>
                  Extras
                </Typography>
                {freeAddOns.map((a) => (
                  <Stack key={a.id} direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CheckCircleRoundedIcon sx={{ color: "success.main", fontSize: 18 }} />
                      <Typography variant="body2">{a.name}</Typography>
                      <Chip
                        size="small"
                        color="success"
                        variant="filled"
                        label="Incluido con tu plan"
                        sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="baseline">
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.disabled",
                          textDecoration: "line-through",
                        }}
                      >
                        {resolveFinalPrice(a).toFixed(2)} €
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "success.dark", fontWeight: 800 }}
                      >
                        Gratis
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
                {paidAddOns.map((a) => (
                  <Stack key={a.id} direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">{a.name}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      +{resolveFinalPrice(a).toFixed(2)} €/mes
                    </Typography>
                  </Stack>
                ))}
              </>
            )}

            {supportAddOn && (
              <>
                <Divider />
                <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 700 }}>
                  Soporte
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">{supportAddOn.name}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    +{resolveFinalPrice(supportAddOn).toFixed(2)} €/mes
                  </Typography>
                </Stack>
              </>
            )}

            <Divider />
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                Total mensual estimado
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 900 }}>
                {monthlyTotal.toFixed(2)}
                <Typography component="span" variant="body2" sx={{ ml: 0.5, color: "text.secondary" }}>
                  €/mes
                </Typography>
              </Typography>
            </Stack>
            <Alert severity="success" icon={<CheckCircleRoundedIcon />} sx={{ mt: 1 }}>
              Sin cobros durante los <strong>primeros 30 días</strong>. Cancela
              en un clic desde tu portal si no quieres continuar.
            </Alert>
          </Stack>
        </Card>
      )}

      <Card sx={{ p: 2, mb: 2, bgcolor: "background.neutral" }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <LockRoundedIcon color="success" />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Pago seguro con Stripe
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Tu tarjeta se procesa en el checkout de Stripe. Nosotros no la
              almacenamos ni la vemos.
            </Typography>
          </Box>
        </Stack>
      </Card>

      <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
        <LoadingButton
          variant="contained"
          size="large"
          loading={paying}
          onClick={handlePay}
          disabled={!plan}
          startIcon={<CreditCardRoundedIcon />}
          sx={{ minWidth: 260, py: 1.5, fontWeight: 800 }}
        >
          Ir al pago
        </LoadingButton>
      </Stack>
    </Box>
  );
}
